import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { chatApi, teamApi, projectsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Send, Paperclip, Hash, User, X, FileText, Smile, Trash2, Briefcase, CheckCheck, Check as CheckIcon, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import EmojiPicker from 'emoji-picker-react';

const ChatPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [searchParams] = useSearchParams();
  const highlightChannel = searchParams.get('highlight');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [channel, setChannel] = useState('general');
  const [channels, setChannels] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [highlightedDM, setHighlightedDM] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const emojiRef = useRef(null);
  const [readersPopup, setReadersPopup] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const loadChannels = async () => {
      try {
        const [membersRes, projectsRes] = await Promise.all([
          teamApi.getMembers(),
          projectsApi.getAll().catch(() => ({ data: [] })),
        ]);
        setTeamMembers(membersRes.data);
        const dmChannels = membersRes.data
          .filter(m => m.user_id !== user?.user_id)
          .map(m => ({ id: `dm_${m.user_id}`, name: m.name, isDM: true, targetUserId: m.user_id }));
        const projectChannels = (projectsRes.data || [])
          .filter(p => p.status !== 'completed' && p.status !== 'cancelled')
          .map(p => ({ id: `project_${p.project_id}`, name: p.name, isDM: false, isProject: true }));
        setChannels([
          { id: 'general', name: 'General', isDM: false },
          ...projectChannels,
          ...dmChannels
        ]);
        if (highlightChannel) {
          setChannel(highlightChannel);
          setHighlightedDM(highlightChannel);
          setTimeout(() => setHighlightedDM(null), 5000);
        }
      } catch {
        setChannels([
          { id: 'general', name: 'General', isDM: false },
        ]);
      }
    };
    loadChannels();
  }, [user, highlightChannel]);

  const fetchMessages = useCallback(async () => {
    try {
      if (channel.startsWith('dm_')) {
        const targetId = channel.replace('dm_', '');
        const myChannel = `dm_${user?.user_id}`;
        const [r1, r2] = await Promise.all([
          chatApi.getMessages(channel),
          chatApi.getMessages(myChannel)
        ]);
        const allMsgs = [
          ...r1.data.filter(m => m.user_id === user?.user_id || m.channel === channel),
          ...r2.data.filter(m => m.user_id === targetId)
        ];
        const seen = new Set();
        const unique = allMsgs.filter(m => {
          if (seen.has(m.message_id)) return false;
          seen.add(m.message_id);
          return true;
        });
        unique.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(unique);
        chatApi.markRead(`dm_${user?.user_id}`).catch(() => {});
        return;
      }
      const r = await chatApi.getMessages(channel);
      setMessages(r.data);
    } catch {}
  }, [channel, user]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Mark messages as read when viewing a channel
  useEffect(() => {
    if (channel) chatApi.markRead(channel).catch(() => {});
  }, [channel, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close emoji picker and readers popup on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
      if (readersPopup && !e.target.closest('[data-readers-popup]') && !e.target.closest('[data-read-receipt]')) setReadersPopup(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [readersPopup]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Archivo muy grande (max 10MB)'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await chatApi.uploadFile(formData);
      setAttachment({ url: r.data.url, filename: r.data.filename, type: file.type });
      toast.success('Archivo adjuntado');
    } catch { toast.error('Error al subir archivo'); }
    finally { setUploading(false); }
    e.target.value = '';
  };

  const handleSend = async () => {
    if (!newMessage.trim() && !attachment) return;
    try {
      const data = { channel, content: newMessage };
      if (attachment) data.attachment = attachment;
      const r = await chatApi.sendMessage(data);
      setMessages(prev => [...prev, r.data]);
      setNewMessage('');
      setAttachment(null);
      setShowMentions(false);
    } catch { toast.error('Error al enviar'); }
  };

  const handleDelete = async (messageId) => {
    try {
      await chatApi.deleteMessage(messageId);
      setMessages(prev => prev.filter(m => m.message_id !== messageId));
      toast.success('Mensaje eliminado');
    } catch { toast.error('Error al eliminar mensaje'); }
  };

  const showReaders = async (messageId, e) => {
    e.stopPropagation();
    if (readersPopup?.messageId === messageId) { setReadersPopup(null); return; }
    try {
      const res = await chatApi.getMessageReaders(messageId);
      const rect = e.currentTarget.getBoundingClientRect();
      setReadersPopup({ messageId, readers: res.data.readers, x: rect.left, y: rect.top - 8 });
    } catch { /* ignore */ }
  };

  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    setShowEmoji(false);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setNewMessage(val);
    // Detect @mention trigger
    const lastAt = val.lastIndexOf('@');
    if (lastAt !== -1 && (lastAt === 0 || val[lastAt - 1] === ' ')) {
      const afterAt = val.slice(lastAt + 1);
      if (!afterAt.includes(' ')) {
        setShowMentions(true);
        setMentionFilter(afterAt.toLowerCase());
        return;
      }
    }
    setShowMentions(false);
  };

  const insertMention = (member) => {
    const lastAt = newMessage.lastIndexOf('@');
    const before = newMessage.slice(0, lastAt);
    setNewMessage(`${before}@${member.name} `);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredMembers = teamMembers.filter(m =>
    m.user_id !== user?.user_id && m.name.toLowerCase().includes(mentionFilter)
  );

  const renderContent = (text) => {
    if (!text) return null;
    // Highlight @mentions
    const parts = text.split(/(@\S+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return <span key={i} className="text-orange-400 font-medium">{part}</span>;
      }
      return part;
    });
  };

  const isImage = (type) => type?.startsWith('image/');
  const isAudio = (type) => type?.startsWith('audio/');

  const renderAttachment = (att) => {
    if (!att) return null;
    const fullUrl = att.url?.startsWith('http') ? att.url : `${att.url}`;
    if (isImage(att.type)) return <img src={fullUrl} alt={att.filename} className="max-w-[240px] rounded-lg mt-1 cursor-pointer" onClick={() => window.open(fullUrl)} />;
    if (isAudio(att.type)) return <audio controls src={fullUrl} className="mt-1 max-w-[240px]" />;
    return (
      <a href={fullUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 mt-1 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-sm">
        <FileText size={16} className="text-orange-400" /> <span className="truncate">{att.filename}</span>
      </a>
    );
  };

  const activeChannel = channels.find?.(c => c.id === channel) || { name: channel, isDM: false };

  return (
    <DashboardLayout title="Chat Interno">
      <div className={cn('flex rounded-xl overflow-hidden border h-[calc(100vh-160px)]', isDark ? 'bg-[#141414] border-white/10' : 'bg-white border-zinc-200')} data-testid="chat-page">
        {/* Channels sidebar */}
        <div className={cn('shrink-0 border-r overflow-y-auto transition-all duration-200', sidebarCollapsed ? 'w-0 overflow-hidden border-r-0' : 'w-48 lg:w-56', isDark ? 'border-white/5 bg-[#0E0E0E]' : 'border-zinc-100 bg-zinc-50')}>
          <div className="p-3">
            <p className={cn('text-xs font-semibold uppercase tracking-wider mb-2', isDark ? 'text-zinc-500' : 'text-zinc-400')}>Canales</p>
            {channels.filter(c => !c.isDM && !c.isProject).map(c => (
              <button key={c.id} onClick={() => setChannel(c.id)} className={cn('w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors mb-0.5', channel === c.id ? 'bg-orange-500/20 text-orange-400' : isDark ? 'text-zinc-400 hover:bg-white/5' : 'text-zinc-600 hover:bg-zinc-100')} data-testid={`channel-${c.id}`}>
                <Hash size={14} /> {c.name}
              </button>
            ))}
            <p className={cn('text-xs font-semibold uppercase tracking-wider mt-4 mb-2', isDark ? 'text-zinc-500' : 'text-zinc-400')}>Mensajes Directos</p>
            {channels.filter(c => c.isDM).map(c => (
              <button key={c.id} onClick={() => { setChannel(c.id); setHighlightedDM(null); }} className={cn('w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all mb-0.5', channel === c.id ? 'bg-orange-500/20 text-orange-400' : highlightedDM === c.id ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/50 animate-pulse' : isDark ? 'text-zinc-400 hover:bg-white/5' : 'text-zinc-600 hover:bg-zinc-100')} data-testid={`dm-${c.id}`}>
                <User size={14} /> {c.name}
                {highlightedDM === c.id && <span className="ml-auto w-2 h-2 rounded-full bg-blue-400 animate-ping" />}
              </button>
            ))}
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className={cn('px-4 py-3 border-b flex items-center gap-2', isDark ? 'border-white/5' : 'border-zinc-100')}>
            <button onClick={() => setSidebarCollapsed(prev => !prev)} className={cn('p-1 rounded hover:bg-zinc-100 transition-colors', isDark ? 'text-zinc-400 hover:bg-white/10' : 'text-zinc-500')} data-testid="toggle-chat-sidebar">
              {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
            {activeChannel.isDM ? <User size={18} className="text-orange-400" /> : activeChannel.isProject ? <Briefcase size={18} className="text-orange-400" /> : <Hash size={18} className="text-orange-400" />}
            <span className={cn('font-medium', isDark ? 'text-white' : 'text-zinc-900')}>{activeChannel.name}</span>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && <p className="text-center text-zinc-500 py-8">No hay mensajes aun. Se el primero en escribir.</p>}
            {messages.map(m => {
              const isMe = m.user_id === user?.user_id;
              const readBy = m.read_by || [];
              const othersRead = readBy.filter(uid => uid !== m.user_id).length > 0;
              return (
                <div key={m.message_id} className={cn('group flex gap-2', isMe && 'flex-row-reverse')} data-testid={`msg-${m.message_id}`}>
                  <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center shrink-0">
                    <span className="text-orange-400 text-xs font-bold">{(m.user_name || '?')[0].toUpperCase()}</span>
                  </div>
                  <div className={cn('max-w-[70%]', isMe && 'text-right')}>
                    <div className="flex items-baseline gap-2" style={isMe ? {flexDirection: 'row-reverse'} : {}}>
                      <span className={cn('text-xs font-medium', isDark ? 'text-zinc-300' : 'text-zinc-700')}>{m.user_name}</span>
                      <span className="text-[10px] text-zinc-500">{m.created_at ? format(new Date(m.created_at), 'HH:mm', { locale: es }) : ''}</span>
                      {(isMe || user?.role === 'admin') && (
                        <button onClick={() => handleDelete(m.message_id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-red-400" data-testid={`delete-msg-${m.message_id}`}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                    {m.content && <p className={cn('text-sm mt-0.5 break-words', isDark ? 'text-zinc-200' : 'text-zinc-800')}>{renderContent(m.content)}</p>}
                    {m.attachment && renderAttachment(m.attachment)}
                    {isMe && (
                      <button onClick={(e) => showReaders(m.message_id, e)} className={cn('inline-flex items-center gap-0.5 mt-0.5 cursor-pointer', othersRead ? 'text-blue-400' : 'text-zinc-500')} title={othersRead ? 'Leido' : 'Enviado'} data-read-receipt data-testid={`read-receipt-${m.message_id}`}>
                        {othersRead ? <CheckCheck size={14} /> : <CheckIcon size={14} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Readers popup */}
          {readersPopup && (
            <div data-readers-popup className={cn('absolute z-50 p-3 rounded-lg shadow-xl border min-w-[160px]', isDark ? 'bg-[#1A1A1A] border-white/10' : 'bg-white border-zinc-200')} style={{ bottom: '100px', right: '40px' }} data-testid="readers-popup">
              <p className={cn('text-xs font-semibold mb-2', isDark ? 'text-zinc-400' : 'text-zinc-500')}>Leido por:</p>
              {readersPopup.readers.filter(r => r.user_id !== user?.user_id).length === 0 ? (
                <p className="text-xs text-zinc-500">Nadie mas</p>
              ) : (
                readersPopup.readers.filter(r => r.user_id !== user?.user_id).map(r => (
                  <div key={r.user_id} className="flex items-center gap-2 py-1">
                    <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <span className="text-blue-400 text-[9px] font-bold">{r.name[0]}</span>
                    </div>
                    <span className={cn('text-xs', isDark ? 'text-zinc-300' : 'text-zinc-700')}>{r.name}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {attachment && (
            <div className={cn('px-4 py-2 border-t flex items-center gap-2', isDark ? 'border-white/5 bg-[#0E0E0E]' : 'border-zinc-100 bg-zinc-50')}>
              <FileText size={14} className="text-orange-400" />
              <span className="text-sm text-zinc-400 truncate">{attachment.filename}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-500" onClick={() => setAttachment(null)}><X size={12} /></Button>
            </div>
          )}

          {/* Mentions dropdown */}
          {showMentions && filteredMembers.length > 0 && (
            <div className={cn('mx-3 mb-1 rounded-lg border shadow-xl max-h-40 overflow-y-auto', isDark ? 'bg-[#1A1A1A] border-white/10' : 'bg-white border-zinc-200')} data-testid="mentions-dropdown">
              {filteredMembers.map(m => (
                <button key={m.user_id} onClick={() => insertMention(m)} className={cn('w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors', isDark ? 'text-zinc-300 hover:bg-white/10' : 'text-zinc-700 hover:bg-zinc-100')} data-testid={`mention-${m.user_id}`}>
                  <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <span className="text-orange-400 text-[10px] font-bold">{m.name[0].toUpperCase()}</span>
                  </div>
                  {m.name}
                </button>
              ))}
            </div>
          )}

          <div className={cn('p-3 border-t flex items-center gap-2 relative', isDark ? 'border-white/5' : 'border-zinc-100')}>
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
            <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} disabled={uploading} className={cn('shrink-0', isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500')} data-testid="chat-attach-btn">
              <Paperclip size={18} />
            </Button>
            <div className="relative" ref={emojiRef}>
              <Button variant="ghost" size="icon" onClick={() => setShowEmoji(!showEmoji)} className={cn('shrink-0', isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500')} data-testid="chat-emoji-btn">
                <Smile size={18} />
              </Button>
              {showEmoji && (
                <div className="absolute bottom-12 left-0 z-50" data-testid="emoji-picker">
                  <EmojiPicker
                    onEmojiClick={onEmojiClick}
                    theme={isDark ? 'dark' : 'light'}
                    width={320}
                    height={400}
                    searchPlaceHolder="Buscar emoji..."
                    previewConfig={{ showPreview: false }}
                  />
                </div>
              )}
            </div>
            <Input ref={inputRef} value={newMessage} onChange={handleInputChange} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Escribe un mensaje... (@mencionar)" className={cn('flex-1', isDark ? 'bg-[#0E0E0E] border-white/10 text-white' : 'bg-zinc-50 border-zinc-200')} data-testid="chat-input" />
            <Button onClick={handleSend} disabled={(!newMessage.trim() && !attachment)} className="bg-orange-500 hover:bg-orange-600 shrink-0" data-testid="chat-send-btn">
              <Send size={18} />
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChatPage;
